import React from 'react';
import { useTranslation } from 'react-i18next';

import { CustomOptionsStatus, ExternalCheckSettingsInput, GuardrailSettingsState } from '../../../../../../gamma-api';
import IbButton from '../../../../../components/common/IbButton';
import IbIcon from '../../../../../components/common/IbIcon';
import IbInput from '../../../../../components/common/IbInput';
import IbNumberInput from '../../../../../components/common/IbNumberInput';
import IbSwitch from '../../../../../components/common/IbSwitch';
import IbTypography from '../../../../../components/common/IbTypography';
import { CodeEditorStatus } from '../../../../../components/common/CodeEditor/types';
import HttpHeadersEditor from '../../../../../components/common/HttpHeadersEditor';
import JsonEditorExpandable from '../../../../../components/common/JsonEditorExpandable';
import {
  HTTP_HEADERS_CLASS_NAME,
  ITEM_CLASS_NAME,
  SECTION_CLASS_NAME,
} from '../../../../../components/common/TypedStepModal/const';
import { UseValidationForm } from '../../../../../utils/validationUtil';
import { ICheckStep } from '../../shared/utils';
import { CheckValidationField, CheckValidationForm } from '../CheckModal.validation';

import {
  CheckBlockMessageField,
  CheckEnabledField,
  CheckErrorPolicyField,
  CheckNameField,
  CheckTimeoutField,
  CheckUseOriginalTextForCheckField,
} from './CommonCheckParameters';
import CheckBehaviorField from './CheckBehaviorField';

export interface IExternalCheckParametersProps {
  step: ICheckStep;
  validationFieldRefs: Partial<Record<CheckValidationField, React.RefObject<HTMLDivElement>>>;
  validationForm: UseValidationForm<CheckValidationForm>;
  onChange: (patch: Partial<ExternalCheckSettingsInput>) => void;
  onDownloadSchema: () => void;
}

const ExternalCheckParameters: React.FC<IExternalCheckParametersProps> = ({
  step,
  validationFieldRefs,
  validationForm,
  onChange,
  onDownloadSchema,
}) => {
  const { t } = useTranslation();
  const check = step.check as ExternalCheckSettingsInput;
  const customOptionsEnabled = check.custom_options?.status === CustomOptionsStatus.Enabled;
  const { validationState, onFieldChange, onFieldBlur, getShowError, getErrorMessage } = validationForm;

  const onStateChange = (state: GuardrailSettingsState) => {
    onChange({ state });
    onFieldChange('state', state);
  };

  const onApiUrlChange = (value: string) => {
    onChange({ api_url: value });
    onFieldChange('apiUrl', value);
  };

  const onCustomOptionsStatusChange = (checked: boolean) => {
    const status = checked ? CustomOptionsStatus.Enabled : CustomOptionsStatus.Disabled;
    onChange({
      custom_options: {
        ...check.custom_options,
        status,
      },
    });
    onFieldChange('customOptionsStatus', status);
  };

  const onCustomOptionsJsonChange = (value?: string) => {
    const jsonValue = value ?? '';
    onChange({
      custom_options: {
        ...check.custom_options,
        json_value: jsonValue,
      },
    });
    onFieldChange('customOptionsJson', jsonValue);
  };

  const onCommonChange = (patch: Partial<ExternalCheckSettingsInput>) => {
    onChange(patch);
  };

  const onApiUrlBlur = () => {
    onFieldBlur('apiUrl');
  };

  const onRequestHeadersChange = (value: Record<string, string>) => {
    onChange({ request_headers: value });
  };

  const onMaxRetriesChange = (value: string | number) => {
    onChange({ max_retries: value as number });
  };

  const onMaxTurnsChange = (value: string | number) => {
    onChange({ max_turns: value as number });
  };

  return (
    <div className={SECTION_CLASS_NAME}>
      <div className={ITEM_CLASS_NAME}>
        <IbTypography.Paragraph type="secondary">
          {t('Agent: Guardrails: ExternalCheck: WebhookDescription')}
        </IbTypography.Paragraph>
        <IbButton icon={<IbIcon iconName="download" />} type="link" onClick={onDownloadSchema}>
          {t('Agent: Guardrails: Webhook: DownloadSchema')}
        </IbButton>
      </div>
      <CheckEnabledField state={check.state} onChange={onStateChange} />
      <CheckNameField check={check} onChange={onCommonChange} />
      <div ref={validationFieldRefs.apiUrl} className={ITEM_CLASS_NAME}>
        <IbTypography.Paragraph strong type="secondary">
          {t('Agent: Guardrails: Webhook: URL: Title')}
        </IbTypography.Paragraph>
        <IbTypography.Paragraph type="secondary">
          {t('Agent: Guardrails: ExternalCheck: URL: Description')}
        </IbTypography.Paragraph>
        <IbInput
          placeholder="https://guardrails.example.com/check"
          status={getShowError(validationState.apiUrl) ? 'error' : 'default'}
          value={check.api_url ?? ''}
          onBlur={onApiUrlBlur}
          onChange={onApiUrlChange}
        />
        {getShowError(validationState.apiUrl) && (
          <IbTypography.Paragraph error type="descriptor">
            {getErrorMessage(validationState.apiUrl, t('Agent: Guardrails: ExternalCheck: URL: Error'))}
          </IbTypography.Paragraph>
        )}
      </div>
      <CheckBehaviorField value={check.behavior} onChange={(behavior) => onCommonChange({ behavior })} />
      <CheckBlockMessageField check={check} onChange={onCommonChange} />
      <CheckErrorPolicyField check={check} onChange={onCommonChange} />
      <CheckTimeoutField check={check} onChange={onCommonChange} />
      <CheckUseOriginalTextForCheckField check={check} onChange={onCommonChange} />
      <div className={ITEM_CLASS_NAME}>
        <IbTypography.Paragraph strong type="secondary">
          {t('Agent: Guardrails: Masking: External: MaxRetries: Title')}
        </IbTypography.Paragraph>
        <IbTypography.Paragraph type="secondary">
          {t('Agent: Guardrails: Masking: External: MaxRetries: Description')}
        </IbTypography.Paragraph>
        <IbNumberInput max={5} min={0} value={check.max_retries} onChange={onMaxRetriesChange} />
      </div>
      <div className={ITEM_CLASS_NAME}>
        <IbTypography.Paragraph strong type="secondary">
          {t('Agent: Guardrails: ExternalCheck: MaxTurns: Title')}
        </IbTypography.Paragraph>
        <IbTypography.Paragraph type="secondary">
          {t('Agent: Guardrails: ExternalCheck: MaxTurns: Description')}
        </IbTypography.Paragraph>
        <IbNumberInput min={0} value={check.max_turns} onChange={onMaxTurnsChange} />
      </div>
      <div className={ITEM_CLASS_NAME}>
        <IbTypography.Paragraph strong type="secondary">
          {t('Agent: Guardrails: Webhook: RequestHeaders: Title')}
        </IbTypography.Paragraph>
        <IbTypography.Paragraph type="secondary">
          {t('Agent: Guardrails: Webhook: RequestHeaders: Description')}
        </IbTypography.Paragraph>
        <HttpHeadersEditor
          className={HTTP_HEADERS_CLASS_NAME}
          value={check.request_headers ?? {}}
          onChange={onRequestHeadersChange}
        />
      </div>
      <div ref={validationFieldRefs.customOptionsJson} className={ITEM_CLASS_NAME}>
        <IbTypography>
          <IbSwitch checked={customOptionsEnabled} size="small" onChange={onCustomOptionsStatusChange} />
          <IbTypography.Paragraph strong type="secondary">
            {t('Agent: Guardrails: Webhook: CustomOptions: Title')}
          </IbTypography.Paragraph>
        </IbTypography>
        <IbTypography.Paragraph type="secondary">
          {t('Agent: Guardrails: Webhook: CustomOptions: Description')}
        </IbTypography.Paragraph>
        <JsonEditorExpandable
          readOnly={!customOptionsEnabled}
          schema={{}}
          status={getShowError(validationState.customOptionsJson) ? CodeEditorStatus.Error : CodeEditorStatus.Default}
          value={check.custom_options?.json_value ?? ''}
          onChange={onCustomOptionsJsonChange}
        />
        {getShowError(validationState.customOptionsJson) && (
          <IbTypography.Paragraph error type="descriptor">
            {getErrorMessage(validationState.customOptionsJson, t('Agent: Guardrails: Webhook: CustomOptions: Error'))}
          </IbTypography.Paragraph>
        )}
      </div>
    </div>
  );
};

export default ExternalCheckParameters;
